import { CompleteKundali } from '../types/kundali';
import {
  LifeStageId,
  LifeStageTaxonomyConfig,
  PlanetaryMaturityStatus,
  LifeStageDashaContext,
  LifeStageGocharContext,
  LifeStageContext
} from '../types/lifeStage';
import { ZODIAC_SIGNS } from './vedicEngine';

// ============================================================================
// 1. Classical Planetary Maturity Table (Parashara / Naisargika)
// ============================================================================
export const PLANETARY_MATURITY_AGES: Record<string, number> = {
  Jupiter: 16,
  Sun: 21,
  Moon: 24,
  Venus: 25,
  Mars: 28,
  Mercury: 32,
  Saturn: 36,
  Rahu: 42,
  Ketu: 48
};

// ============================================================================
// 2. 8-Tier Configurable Life Stage Taxonomy Registry
// ============================================================================
export const LIFE_STAGE_CONFIGS: Record<LifeStageId, LifeStageTaxonomyConfig> = {
  early_childhood: {
    id: 'early_childhood',
    minAge: 0,
    maxAge: 10,
    labelEn: 'Early Childhood',
    labelHi: 'प्रारंभिक बाल्यावस्था',
    subLabelEn: 'Playful Discovery & Cognitive Baseline',
    subLabelHi: 'जिज्ञासा, खेल-कूद एवं सुरक्षित परिवेश',
    developmentalFocusEn: 'Formative curiosity, cognitive foundations, emotional security, and stress-free learning.',
    developmentalFocusHi: 'स्वाभाविक जिज्ञासा, बौद्धिक नींव, भावनात्मक सुरक्षा एवं खेल-खेल में सीखना।',
    primaryPurushartha: 'Dharma',
    primaryHouseFocus: [1, 4, 5],
    inappropriatePredictiveDomains: ['career_job', 'business', 'marriage_relationship', 'wealth_finance', 'litigation_competition', 'house_property', 'vehicle']
  },
  adolescence: {
    id: 'adolescence',
    minAge: 11,
    maxAge: 17,
    labelEn: 'Adolescence & Skill Discovery',
    labelHi: 'किशोरावस्था एवं प्रतिभा विकास',
    subLabelEn: 'Identity Formation & Academic Stream',
    subLabelHi: 'आत्म-पहचान, अध्ययन एवं प्रतिभा अन्वेषण',
    developmentalFocusEn: 'Academic specialization, emotional self-regulation, peer empathy, and creative talent cultivation.',
    developmentalFocusHi: 'अध्ययन शाखा चयन, भावनात्मक संतुलन, सहपाठी सामंजस्य एवं रचनात्मक प्रतिभा निखार।',
    primaryPurushartha: 'Dharma',
    primaryHouseFocus: [4, 5, 9],
    inappropriatePredictiveDomains: ['marriage_relationship', 'business', 'litigation_competition']
  },
  young_adult: {
    id: 'young_adult',
    minAge: 18,
    maxAge: 24,
    labelEn: 'Young Adult / Foundation',
    labelHi: 'युवा अवस्था एवं आधार निर्माण',
    subLabelEn: 'Higher Scholarship & Vocational Launch',
    subLabelHi: 'उच्च शिक्षा, आजीविका प्रारंभ एवं स्वतंत्रता',
    developmentalFocusEn: 'Higher education, vocational specialization, internships, high-agency initiative, and early network.',
    developmentalFocusHi: 'उच्च शिक्षा, व्यावसायिक कौशल, आजीविका प्रारंभ एवं स्वतंत्र निर्णय क्षमता।',
    primaryPurushartha: 'Artha',
    primaryHouseFocus: [1, 5, 9, 10],
    inappropriatePredictiveDomains: []
  },
  establishment: {
    id: 'establishment',
    minAge: 25,
    maxAge: 35,
    labelEn: 'Establishment & Execution',
    labelHi: 'प्रतिष्ठापन एवं गृहस्थ धर्म',
    subLabelEn: 'Career Architecture & Asset Compounding',
    subLabelHi: 'करियर उत्थान, संपदा निर्माण एवं वैवाहिक स्थायित्व',
    developmentalFocusEn: 'Career consolidation, enterprise building, marital partnership, wealth compounding, and family foundation.',
    developmentalFocusHi: 'करियर में स्थिरता, व्यापार/नौकरी में प्रगति, वैवाहिक जीवन एवं दीर्घकालिक संपत्ति निर्माण।',
    primaryPurushartha: 'Artha',
    primaryHouseFocus: [2, 7, 10, 11],
    inappropriatePredictiveDomains: []
  },
  consolidation: {
    id: 'consolidation',
    minAge: 36,
    maxAge: 48,
    labelEn: 'Expansion & Consolidation',
    labelHi: 'विस्तार एवं सुदृढ़ीकरण',
    subLabelEn: 'Executive Authority & Asset Governance',
    subLabelHi: 'प्रशासनिक नेतृत्व, संपत्ति प्रबंधन एवं पारिवारिक दायित्व',
    developmentalFocusEn: 'Executive leadership, organizational authority, fixed asset governance, progeny upbringing, and institutional leverage.',
    developmentalFocusHi: 'नेतृत्व क्षमता, संपत्ति सुरक्षा, संतान मार्गदर्शन एवं सामाजिक प्रभाव विस्तार।',
    primaryPurushartha: 'Artha',
    primaryHouseFocus: [4, 5, 9, 10, 11],
    inappropriatePredictiveDomains: []
  },
  maturity_reorient: {
    id: 'maturity_reorient',
    minAge: 49,
    maxAge: 60,
    labelEn: 'Senior Advisory & Mentorship',
    labelHi: 'वरिष्ठ सलाहकार एवं मार्गदर्शक',
    subLabelEn: 'Strategic Mentorship & Capital Allocation',
    subLabelHi: 'सलाहकार भूमिका, पूंजी प्रबंधन एवं दार्शनिक दृष्टि',
    developmentalFocusEn: 'Strategic advisory, executive mentorship, portfolio capital management, health stewardship, and spiritual balance.',
    developmentalFocusHi: 'वरिष्ठ मार्गदर्शन, परामर्श, स्वास्थ्य संतुलन एवं आध्यात्मिक अभिरुचि।',
    primaryPurushartha: 'Dharma',
    primaryHouseFocus: [9, 10, 11, 12],
    inappropriatePredictiveDomains: []
  },
  legacy_wisdom: {
    id: 'legacy_wisdom',
    minAge: 61,
    maxAge: 75,
    labelEn: 'Legacy & Elder Wisdom',
    labelHi: 'वरिष्ठ प्रज्ञा एवं परंपरा संरक्षण',
    subLabelEn: 'Dharma Integration & Knowledge Transfer',
    subLabelHi: 'धर्म निष्ठा, ज्ञान हस्तांतरण एवं मानसिक शांति',
    developmentalFocusEn: 'Intellectual legacy, knowledge transfer, generational blessing, spiritual sadhana, and energetic conservation.',
    developmentalFocusHi: 'अनुभवों का लाभ नई पीढ़ी को देना, आध्यात्मिक साधना एवं शांतिपूर्ण जीवन।',
    primaryPurushartha: 'Moksha',
    primaryHouseFocus: [5, 9, 12],
    inappropriatePredictiveDomains: []
  },
  advanced_longevity: {
    id: 'advanced_longevity',
    minAge: 76,
    maxAge: 120,
    labelEn: 'Advanced Longevity & Moksha',
    labelHi: 'दीर्घायु एवं मोक्ष साधना',
    subLabelEn: 'Spiritual Sanctuary & Inner Completion',
    subLabelHi: 'आत्म-शांति, ईश्वर चिंतन एवं मोक्ष मार्ग',
    developmentalFocusEn: 'Spiritual detachment, profound contemplation, inner peace, and surrender to divine consciousness.',
    developmentalFocusHi: 'आत्म-शांति, ईश्वर चिंतन, वैराग्य एवं मोक्ष प्राप्ति की ओर अग्रसर।',
    primaryPurushartha: 'Moksha',
    primaryHouseFocus: [8, 9, 12],
    inappropriatePredictiveDomains: []
  }
};

// ============================================================================
// 3. Exact Leap-Year Safe Age Calculation
// ============================================================================
export function calculateExactAge(birthDateStr?: string, targetDate: Date = new Date()): {
  exactAge: number;
  years: number;
  months: number;
  days: number;
} {
  if (!birthDateStr) {
    return { exactAge: 30, years: 30, months: 0, days: 0 };
  }

  const bDate = new Date(birthDateStr);
  if (isNaN(bDate.getTime())) {
    return { exactAge: 30, years: 30, months: 0, days: 0 };
  }

  let years = targetDate.getFullYear() - bDate.getFullYear();
  let months = targetDate.getMonth() - bDate.getMonth();
  let days = targetDate.getDate() - bDate.getDate();

  if (days < 0) {
    months--;
    // Days in previous month
    const prevMonthLastDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), 0).getDate();
    days += prevMonthLastDay;
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  if (years < 0) {
    years = 0;
    months = 0;
    days = 0;
  }

  const exactAge = Number((years + months / 12 + days / 365.25).toFixed(2));

  return { exactAge, years, months, days };
}

// ============================================================================
// 4. Resolve Life Stage by Age
// ============================================================================
export function getLifeStageByAge(ageInYears: number): LifeStageTaxonomyConfig {
  const rounded = Math.floor(ageInYears);
  if (rounded <= 10) return LIFE_STAGE_CONFIGS.early_childhood;
  if (rounded <= 17) return LIFE_STAGE_CONFIGS.adolescence;
  if (rounded <= 24) return LIFE_STAGE_CONFIGS.young_adult;
  if (rounded <= 35) return LIFE_STAGE_CONFIGS.establishment;
  if (rounded <= 48) return LIFE_STAGE_CONFIGS.consolidation;
  if (rounded <= 60) return LIFE_STAGE_CONFIGS.maturity_reorient;
  if (rounded <= 75) return LIFE_STAGE_CONFIGS.legacy_wisdom;
  return LIFE_STAGE_CONFIGS.advanced_longevity;
}

// ============================================================================
// 5. Planetary Maturation Tracker
// ============================================================================
export function evaluatePlanetaryMaturation(ageInYears: number): PlanetaryMaturityStatus[] {
  const planets = Object.keys(PLANETARY_MATURITY_AGES);
  return planets.map((planet) => {
    const naturalMaturityAge = PLANETARY_MATURITY_AGES[planet];
    const isMatured = ageInYears >= naturalMaturityAge;
    const yearsSinceMaturity = isMatured ? Number((ageInYears - naturalMaturityAge).toFixed(1)) : 0;
    return {
      planet,
      naturalMaturityAge,
      isMatured,
      yearsSinceMaturity
    };
  });
}

// ============================================================================
// 6. Active Dasha Context Builder
// ============================================================================
export function extractActiveDashaContext(
  kundali: CompleteKundali,
  targetDate: Date = new Date()
): LifeStageDashaContext {
  const nowTs = targetDate.getTime();
  let mahadasha = 'Jupiter';
  let antardasha = 'Jupiter';
  let pratyantardasha = 'Jupiter';
  let startDate = new Date().toISOString().split('T')[0];
  let endDate = new Date(Date.now() + 365 * 86400000 * 2).toISOString().split('T')[0];

  const birthDateStr = kundali.birthDetails?.date || '1990-01-01';
  const birthTs = new Date(birthDateStr).getTime();

  if (kundali.dashas && Array.isArray(kundali.dashas) && kundali.dashas.length > 0) {
    const currentMD = kundali.dashas.find((d) => {
      const start = new Date(d.startDate).getTime();
      const end = new Date(d.endDate).getTime();
      return nowTs >= start && nowTs <= end;
    }) || kundali.dashas[0];

    if (currentMD) {
      mahadasha = currentMD.planet;
      startDate = currentMD.startDate;
      endDate = currentMD.endDate;

      if (currentMD.antardashas && currentMD.antardashas.length > 0) {
        const currentAD = currentMD.antardashas.find((ad) => {
          const start = new Date(ad.startDate).getTime();
          const end = new Date(ad.endDate).getTime();
          return nowTs >= start && nowTs <= end;
        }) || currentMD.antardashas[0];

        if (currentAD) {
          antardasha = currentAD.planet;
          startDate = currentAD.startDate;
          endDate = currentAD.endDate;

          if (currentAD.antardashas && currentAD.antardashas.length > 0) {
            const currentPD = currentAD.antardashas.find((pd) => {
              const start = new Date(pd.startDate).getTime();
              const end = new Date(pd.endDate).getTime();
              return nowTs >= start && nowTs <= end;
            }) || currentAD.antardashas[0];

            if (currentPD) {
              pratyantardasha = currentPD.planet;
            }
          }
        }
      }
    }
  }

  const dashaStartTs = new Date(startDate).getTime();
  const dashaEndTs = new Date(endDate).getTime();
  const dashaAgeAtStart = Number(Math.max(0, (dashaStartTs - birthTs) / (365.25 * 86400000)).toFixed(1));
  const dashaAgeAtEnd = Number(Math.max(0, (dashaEndTs - birthTs) / (365.25 * 86400000)).toFixed(1));

  return {
    mahadasha,
    antardasha,
    pratyantardasha,
    startDate,
    endDate,
    dashaAgeAtStart,
    dashaAgeAtEnd,
    dashaThemeEn: `${mahadasha} Mahadasha with ${antardasha} Antardasha`,
    dashaThemeHi: `${mahadasha} महादशा में ${antardasha} अंतर्दशा का प्रभाव`
  };
}

// ============================================================================
// 7. Active Gochar Context Builder
// ============================================================================
export function extractActiveGocharContext(
  kundali: CompleteKundali
): LifeStageGocharContext {
  const moonPlanet = kundali.planets?.find((p) => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Taurus';
  const lagnaSign = kundali.houses?.[0]?.sign || 'Aries';

  const moonSignIdx = Math.max(0, ZODIAC_SIGNS.indexOf(moonSign as any));
  const lagnaSignIdx = Math.max(0, ZODIAC_SIGNS.indexOf(lagnaSign as any));

  // Current transit approximations: Saturn in Pisces (index 11), Jupiter in Taurus/Gemini (index 1/2)
  const saturnTransitSignIdx = 11; // Pisces
  const jupiterTransitSignIdx = 1;  // Taurus

  const saturnFromMoon = ((saturnTransitSignIdx - moonSignIdx + 12) % 12) + 1;
  const saturnFromLagna = ((saturnTransitSignIdx - lagnaSignIdx + 12) % 12) + 1;

  const isSadeSatiActive = saturnFromMoon === 12 || saturnFromMoon === 1 || saturnFromMoon === 2;
  let sadeSatiPhase: 'rising' | 'peak' | 'setting' | undefined = undefined;
  if (saturnFromMoon === 12) sadeSatiPhase = 'rising';
  else if (saturnFromMoon === 1) sadeSatiPhase = 'peak';
  else if (saturnFromMoon === 2) sadeSatiPhase = 'setting';

  const isDhaiyaActive = saturnFromMoon === 4 || saturnFromMoon === 8;

  const jupiterFromMoon = ((jupiterTransitSignIdx - moonSignIdx + 12) % 12) + 1;
  const jupiterFromLagna = ((jupiterTransitSignIdx - lagnaSignIdx + 12) % 12) + 1;

  const isJupiterGuruBalActive = [2, 5, 7, 9, 11].includes(jupiterFromMoon);

  let gocharSummaryEn = `Saturn is transiting House ${saturnFromMoon} from Moon and House ${saturnFromLagna} from Lagna.`;
  if (isSadeSatiActive) {
    gocharSummaryEn += ` Active Sade Sati (${sadeSatiPhase} phase).`;
  } else if (isDhaiyaActive) {
    gocharSummaryEn += ` Active Kantaka/Ashtama Shani Dhaiya.`;
  }
  if (isJupiterGuruBalActive) {
    gocharSummaryEn += ` Favorable Jupiter transit (Guru Bala active in 5/9/11).`;
  }

  let gocharSummaryHi = `शनि चंद्रमा से ${saturnFromMoon}वें और लग्न से ${saturnFromLagna}वें भाव में गोचर कर रहे हैं।`;
  if (isSadeSatiActive) {
    gocharSummaryHi += ` साढ़ेसाती का प्रभाव (${sadeSatiPhase === 'rising' ? 'प्रारंभिक' : sadeSatiPhase === 'peak' ? 'मध्य' : 'अंतिम'} चरण)।`;
  }
  if (isJupiterGuruBalActive) {
    gocharSummaryHi += ` गुरु बल अनुकूल स्थिति में सक्रिय है।`;
  }

  return {
    saturnTransitHouseFromMoon: saturnFromMoon,
    saturnTransitHouseFromLagna: saturnFromLagna,
    isSadeSatiActive,
    sadeSatiPhase,
    isDhaiyaActive,
    jupiterTransitHouseFromMoon: jupiterFromMoon,
    jupiterTransitHouseFromLagna: jupiterFromLagna,
    isJupiterGuruBalActive,
    gocharSummaryEn,
    gocharSummaryHi
  };
}

// ============================================================================
// 8. Master Life Stage Context Engine
// ============================================================================
export function computeLifeStageContext(
  kundali: CompleteKundali,
  targetDate: Date = new Date()
): LifeStageContext {
  const { exactAge, years, months } = calculateExactAge(kundali.birthDetails?.date, targetDate);
  const stage = getLifeStageByAge(exactAge);

  const isYoungChild = exactAge < 11; // Preserves strict backward compatibility
  const isAdolescent = exactAge >= 11 && exactAge <= 17;
  const isYoungAdult = exactAge >= 18 && exactAge <= 24;
  const isAdult = exactAge >= 18;
  const isSenior = exactAge >= 61;

  const maturationStatuses = evaluatePlanetaryMaturation(exactAge);
  const activeDashaContext = extractActiveDashaContext(kundali, targetDate);
  const gocharContext = extractActiveGocharContext(kundali);

  // Desh-Kaal-Patra Synthesis Guidance
  let deshKaalPatraGuidanceEn = '';
  let deshKaalPatraGuidanceHi = '';

  if (isYoungChild) {
    deshKaalPatraGuidanceEn = `Native is in early childhood (${exactAge} years). Focus strictly on cognitive curiosity, emotional security, learning habits, and supportive parenting. Predictive adult vocational or marriage interpretations are strictly non-applicable.`;
    deshKaalPatraGuidanceHi = `जातक वर्तमान में प्रारंभिक बाल्यावस्था (${exactAge} वर्ष) में है। केवल स्वाभाविक जिज्ञासा, अध्ययन की आदतें, और पारिवारिक स्नेह पर ध्यान केंद्रित करें। वयस्क करियर या विवाह संबंधी भविष्यवाणियां इस आयु में अनुचित हैं।`;
  } else if (isAdolescent) {
    deshKaalPatraGuidanceEn = `Native is in adolescence (${exactAge} years). Focus on academic stream selection, creative aptitude, peer emotional harmony, and disciplined talent cultivation.`;
    deshKaalPatraGuidanceHi = `जातक किशोरावस्था (${exactAge} वर्ष) में है। ध्यान मुख्य रूप से उच्च विद्यालय शिक्षा, रचनात्मक प्रतिभा और भावनात्मक स्थिरता पर होना चाहिए।`;
  } else if (isYoungAdult) {
    deshKaalPatraGuidanceEn = `Native is in young adulthood (${exactAge} years). Focus on higher scholarship, zero-to-one vocational launch, independence, and foundational mastery.`;
    deshKaalPatraGuidanceHi = `जातक युवा अवस्था (${exactAge} वर्ष) में है। उच्च शिक्षा, करियर की मजबूत शुरुआत और स्वतंत्र निर्णय क्षमता पर मार्गदर्शन केंद्रित है।`;
  } else if (stage.id === 'establishment') {
    deshKaalPatraGuidanceEn = `Native is in the Establishment phase (${exactAge} years, Artha/Grihastha). Frame analysis around career acceleration, strategic capital compounding, and marital stability.`;
    deshKaalPatraGuidanceHi = `जातक प्रतिष्ठापन चरण (${exactAge} वर्ष) में है। करियर में पदोन्नति, वित्तीय विकास एवं पारिवारिक सुदृढ़ीकरण पर ध्यान केंद्रित है।`;
  } else if (stage.id === 'consolidation') {
    deshKaalPatraGuidanceEn = `Native is in the Consolidation phase (${exactAge} years). Focus on executive authority, organizational scaling, asset protection, and lineage guidance.`;
    deshKaalPatraGuidanceHi = `जातक सुदृढ़ीकरण चरण (${exactAge} वर्ष) में है। प्रशासनिक नेतृत्व, संपत्ति प्रबंधन एवं संतान के मार्गदर्शन पर बल दें।`;
  } else if (stage.id === 'maturity_reorient') {
    deshKaalPatraGuidanceEn = `Native is in the Senior Advisory & Mentorship phase (${exactAge} years). Focus on strategic mentorship, portfolio capital allocation, and philosophical equilibrium.`;
    deshKaalPatraGuidanceHi = `जातक वरिष्ठ सलाहकार एवं मार्गदर्शक चरण (${exactAge} वर्ष) में है। सलाहकार नेतृत्व, पूंजी सुरक्षा एवं आध्यात्मिक शांति का समन्वय आवश्यक है।`;
  } else {
    deshKaalPatraGuidanceEn = `Native is in the Legacy & Wisdom phase (${exactAge} years, Moksha focus). Emphasize spiritual peace, intellectual legacy, and generational blessing.`;
    deshKaalPatraGuidanceHi = `जातक वरिष्ठ प्रज्ञा चरण (${exactAge} वर्ष) में है। आध्यात्मिक साधना, मानसिक शांति एवं ज्ञान हस्तांतरण को प्राथमिकता दें।`;
  }

  return {
    exactAge,
    years,
    months,
    stage,
    isYoungChild,
    isAdolescent,
    isYoungAdult,
    isAdult,
    isSenior,
    maturationStatuses,
    activeDashaContext,
    gocharContext,
    deshKaalPatraGuidanceEn,
    deshKaalPatraGuidanceHi
  };
}
